import React from 'react'

import { GraphFlowsResponse } from 'src/api/gen'
import SankeyPlot from 'src/commons/graphs/SankeyPlot'
import { IColoredLink, IColoredNode, IOriginDestinationSankey } from 'src/commons/types'

const SMALL_PALETTE = [
  '#8EAFFF', // malibu blue
  '#93FF93', // mint green
  '#FFB393', // Mona Lisa orange
]
const LARGE_PALETTE = [
  '#B81414', // thunderbird red
  '#FF0119', // red
  '#FF7700', // flush orange
  '#FFCE05', // supernova yellow
  '#FEFE00', // yellow
  '#BDEF03', // lime green
  '#6AD2A9', // downy green
  '#0289A2', // deep cerulean blue
  '#43DAE6', // picton blue
  '#4C80FD', // dodger blue
  '#4601FF', // electric violet
  '#9307FE', // violet
  '#F42FB8', // persian pink
  '#FF96DF', // lavender pink
  '#A4A4A4', // grey
]

interface IOriginDestinationSankeyProps {
  data: GraphFlowsResponse
  showInnerTraffic?: boolean
  title?: string
}

function getNodes(
  flows: { [origin_label: string]: { [destinationLabel: string]: number } },
  showInnerTraffic: boolean,
  palette: string[]
): IColoredNode[] {
  let index = 0
  const destinationZones: string[] = []
  for (const [origin, destinations] of Object.entries(flows)) {
    for (const [destination, flow] of Object.entries(destinations)) {
      if (flow > 0 && (showInnerTraffic || destination !== origin)) {
        destinationZones.push(destination)
      }
    }
  }
  const nodes: IColoredNode[] = []
  for (const [origin, destinations] of Object.entries(flows)) {
    // Only add to origin nodes if there are trips leaving the zone
    // Only count inner traffic if needed
    let sum = 0
    for (const [destination, flow] of Object.entries(destinations)) {
      if (showInnerTraffic || destination !== origin) {
        sum += flow
      }
    }
    if (sum > 0) {
      nodes.push({ name: origin, key: `origin-${origin}`, color: palette[index] })
    }
    // Only add to destination nodes if there are trips ending in the zone
    if (destinationZones.includes(origin)) {
      nodes.push({
        name: origin,
        key: `destination-${origin}`,
        color: palette[index],
      })
    }
    ++index
  }
  return nodes
}

function getLinks(
  nodes: IColoredNode[],
  flows: { [origin_label: string]: { [destinationLabel: string]: number } },
  showInnerTraffic: boolean,
  palette: string[]
): IColoredLink[] {
  const links: IColoredLink[] = []
  for (const [origin, destinations] of Object.entries(flows)) {
    const originIndex = nodes.findIndex((node) => node.key === `origin-${origin}`)

    for (const [destinationLabel, numberOfTrips] of Object.entries(destinations)) {
      if (!showInnerTraffic && destinationLabel === origin) {
        continue
      }
      if (numberOfTrips > 0) {
        const destinationIndex = nodes.findIndex((node) => node.key === `destination-${destinationLabel}`)
        links.push({
          source: originIndex,
          target: destinationIndex,
          value: numberOfTrips,
          color: palette[originIndex / 2],
        })
      }
    }
  }
  return links
}

export function computeOriginDestination(
  graphFlowsReponse: GraphFlowsResponse,
  showInnerTraffic: boolean
): IOriginDestinationSankey {
  if (!graphFlowsReponse.flows) {
    return { nodes: [], links: [] }
  }

  const palette = Object.keys(graphFlowsReponse.flows).length > 3 ? LARGE_PALETTE : SMALL_PALETTE
  const nodes = getNodes(graphFlowsReponse.flows, showInnerTraffic, palette)
  const links = getLinks(nodes, graphFlowsReponse.flows, showInnerTraffic, palette)
  return {
    nodes,
    links,
  }
}

export default function OriginDestinationSankey(props: IOriginDestinationSankeyProps) {
  const { nodes = [], links = [] } = computeOriginDestination(
    props.data,
    props.showInnerTraffic == undefined ? true : props.showInnerTraffic
  )
  return <SankeyPlot nodes={nodes} links={links} />
}
